import * as angular from 'angular';
import {IHttpPromise, IHttpResponse, IPromise} from 'angular';
import {
    BoundingBox,
    CopiableGeometryFeature,
    DisplayFeature,
    FeatureAttributeChange,
    HeightReference,
    LockState,
    NvdbFeature, SidePosition,
    SidePositionEdit
} from '../../presentation/routes/contract-features-3/domain';
import {NvdbSubmission, NvdbSubmissionRequest, SubmitabilityReport} from '../representation/nvdb-submission';
import {LatLng} from 'leaflet';
import {default as MapUtils, PositionJson} from '../../presentation/map/map-utils';
import {
    Comment,
    Contract,
    ContractLock,
    ContractMilestone,
    ContractObjectLists,
    ContractType,
    Contributor,
    Destination,
    MapComment
} from '../representation/contract';
import {Event} from '../representation/event';
import {User} from '../representation/user';
import {Feature, FeaturesResult, FeatureTypeIdWithValidOperations, Operation} from '../representation/feature';
import messagesService from '../../service/messages/messages-service';
import {FkbAlias} from '../representation/fkb';
import {LaneUpdate} from '../representation/lane';
import {ContractFile, SosiFileLine} from '../representation/file';
import {LocalFile} from '../../model/model-filelist';
import {
    Association,
    AssociationResult,
    AssociationsMultiResponse,
    AutomaticAssociationParameters,
    ManualAssociationParameters
} from '../representation/association';
import {CompletenessItem, FeatureTypeSummary} from '../representation/feature-type';
import {MessageDataResponse, MessageStatusResponse} from '../representation';
import {ExcelParseResult} from '../representation/object-list';
import {RoadSegment} from '../representation/road-segment';
import {ContractGroup, ContractGroupWithData} from '../representation/contract-group';
import {VersionInfo} from '../representation/version-info';
import {FeatureTypeModel} from '../representation/viewmodel';
import {LocalizationChange} from "../representation/localization-change";
import {SortBy, SortOrder, SortSetting} from "../../presentation/routes/contract-features-3/domain/FeaturesState";
import {HttpCacheService} from "./cache.service";

export default class server {
    public messages: messagesService;
    static $inject: string[];
    constructor(private $httpCacheService: HttpCacheService, private $q: angular.IQService, messages: messagesService) {
        this.messages = messages;
    }

    registerReviewChangelogEvent(): IHttpPromise<void> {
        return this.$httpCacheService.post('/session/review_changelog', null);
    }

    externalResourcesHealthy(): IPromise<boolean> {
        function checkResponseStatus(response: IHttpResponse<void>) {
            return response.status === 200;
        }

        return this.$httpCacheService.get('/status/healthchecks', description('Helsesjekk for eksterne ressurser'))
            .then(checkResponseStatus, checkResponseStatus);
    }

    getAssociationDistanceLimit(): IPromise<number> {
        return this.$httpCacheService.get<number>('/rest/config/associationdistancelimit', description('Hente maksimum tillatte avstand mellom assosierte vegobjekter'))
            .then(server.unwrapResponseData);
    }

    getNvdbReadApiUrl(): IPromise<string> {
        return this.$httpCacheService.get<string>('/rest/config/nvdbreadapiurl', description('Hente endepunkt for NVDB API Les')).then(server.unwrapResponseData);
    }

    saveObjectLists(contractId: string, objectLists: ContractObjectLists): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/objectlists', objectLists, description('Lagre objektliste'));
    }

    readObjectLists(contractId: string): IPromise<ContractObjectLists> {
        return this.$httpCacheService.get<ContractObjectLists>('/rest/contract/' + contractId + '/objectlists', description('Lese objektliste'))
            .then(server.unwrapResponseData);
    }

    getFeatureTypeSummary(contractId: string): IPromise<FeatureTypeSummary[]> {
        return this.$httpCacheService.get<FeatureTypeSummary[]>(`/rest/vegobjekt/${contractId}/typeliste` , description('Lese vegobjekttyper for visning'))
            .then(server.unwrapResponseData);
    }

    getFeatures(contractId: string, featureTypeId: number, paging?: {page: number, pageSize?: number, search?: string, order?: SortSetting}): IPromise<FeatureTypeModel> {
        let params = '';
        if (paging) {
            params = `?page=${paging.page ?? 0}`;
            if (paging.pageSize) {
                params += `&pageSize=${paging.pageSize}`;
            }
            if (paging.search && paging.search.trim().length > 0) {
                params += `&search=${paging.search}`;
            }
            if(paging.order && paging.order.sortBy) {
                params += '';
                switch (paging.order.sortBy) {
                    case SortBy.NAME:
                        params += '&orderByType=NAME';
                        break;
                    case SortBy.OPERATION:
                        params += '&orderByType=OPERATION';
                        break;
                    case SortBy.SIDE_POSITION:
                        params += '&orderByType=SIDE_POSITION';
                        break;
                    case SortBy.LANE:
                        params += '&orderByType=LANE';
                        break;
                    case SortBy.ROAD:
                        params += '&orderByType=ROAD_SYSTEM_REFERENCE';
                        break;
                    case SortBy.ATTRIBUTE:
                        if (paging.order.attribute) {
                            params += `&orderByType=ATTRIBUTE&orderByValue=${paging.order.attribute.id}`;
                        }
                        break;
                    case SortBy.QUALITY_PARAMETER:
                        if (paging.order.qualityParam) {
                            params += `&orderByType=QUALITY_PARAMETER&orderByValue=${paging.order.qualityParam.key}`;
                        }
                        break;
                }
                params += `&direction=${paging.order?.sortOrder === SortOrder.ASC ? 'ASC' : 'DESC'}`;
            }
        }

        return this.$httpCacheService.get<FeatureTypeModel>(`/rest/contract/${contractId}/featuretype/${featureTypeId}${params}`,
                                             description('Lese vegobjekter for visning'))
            .then(server.unwrapResponseData);
    }

    /**
     * @deprecated Remains of an earlier data model, should not be used for new code.
     */
    // XXX No typings for this, as we want to remove the model
    getFeaturesViewModel(contractId: string): IPromise<any> {
        return this.$httpCacheService.get('/rest/contract/' + contractId + '/features_view_model', description('Lese vegobjekter for visning'))
            .then(server.unwrapResponseData);
    }

    /**
     * @deprecated Remains of an earlier data model, should not be used for new code.
     */
    // XXX No typings for this, as we want to remove the model
    getFkbFeaturesViewModel(contractId: string): IPromise<any> {
        return this.$httpCacheService.get('/rest/contract/' + contractId + '/fkb_features_view_model', description('Lese vegobjekter for visning'))
            .then(server.unwrapResponseData);
    }

    checkSensitiveAttributeTypes(contractId: string, url: string, operation: Operation): IPromise<boolean> {
        const queryParams = `?operation=${operation}`;
        return this.$httpCacheService.post<boolean>(`/rest/contract/${contractId}/sensitive_attributes_on_url${queryParams}`,
            url,
            description('Sjekke om det finnes sensitive elementer på en URL andre ikke har lov til å se'))
            .then(server.unwrapResponseData);
    }

    getMissingAttributeTypes(contractId: string, url: string): IPromise<FeaturesResult> {
        const queryParams = '?url=' + url;

        return this.$httpCacheService.get<FeaturesResult>('/rest/contract/' + contractId + '/missing_attribute_types' + queryParams,
            description('Sjekke om det finnes sensitive rettigheter du ikke har lov til å se'))
            .then(server.unwrapResponseData);
    }

    addExistingFeatures(contractId: string, url: string, operation: string): IPromise<MessageStatusResponse> {
        const modifiedUrl = url.replace('nvdbapiles.atlas.vegvesen.no', 'nvdbapiles-v3.atlas.vegvesen.no');
        return this.$httpCacheService.post<MessageStatusResponse>('/rest/contract/' + contractId + '/nvdb_features' + '?operation=' + operation, modifiedUrl,
                                                      description('Import fra NVDB'))
            .then(server.unwrapResponseData);
    }

    readFeatures(contractId: string, destination: Destination): IPromise<FeaturesResult> {
        let queryParams = '';
        if (destination) {
            queryParams = '?destination=' + destination;
        }

        return this.$httpCacheService.get<FeaturesResult>('/rest/contract/' + contractId + '/feature' + queryParams, description('Lese vegobjekter'))
            .then(server.unwrapResponseData)
            .then((data) => {
                return data;
            });
    }

    createFeature(contractId: string, featureTypeId: number, name: string, wkt: string, localize: boolean, associate: string[]): IPromise<NvdbFeature> {
        return this.$httpCacheService.post<NvdbFeature>(`/rest/contract/${contractId}/feature/create`, {typeId: featureTypeId, name, wkt, localize, associate}, description('Opprette vegobjekt'))
            .then(server.unwrapResponseData)
            .then((result) => {
                return result;
            });
    }

    updateAllFeaturesWithAttributeValue(contractId: string, featureId: string, attributeTypeId: number, visibleFeatures: string[]): IPromise<Feature[]> {
        return this.$httpCacheService.put<Feature[]>(`/rest/contract/${contractId}/feature/${featureId}/copy-attribute/${attributeTypeId}`, visibleFeatures)
            .then(server.unwrapResponseData);
    }

    updateFeatures(contractId: string, changes: FeatureAttributeChange[]): IPromise<Feature[]> {
        return this.$httpCacheService.post<Feature[]>('/rest/contract/' + contractId + '/feature/edit', changes, description('Oppdatere vegobjekter'))
            .then(server.unwrapResponseData)
            .then((result) => {
                return result;
            });
    }

    approveLocationals(contractId: string, featureIds: string[]): IPromise<void> {
        return this.$httpCacheService
            .post<void>(`/rest/contract/${contractId}/locational/confirm`, featureIds)
            .then(server.unwrapResponseData);
    }

    localizeFeature2(contractId: string, roadObjectId: string, roadSegment: RoadSegment, startAnchor: LatLng, endAnchor: LatLng): IPromise<LocalizeResult> {
        const localizeArgs: LocalizeArguments = {
            roadObjectId,
            roadSegmentJson: roadSegment,
            startAnchor: startAnchor ? MapUtils.wgs84ToUtm33(startAnchor) : null,
            endAnchor: endAnchor ? MapUtils.wgs84ToUtm33(endAnchor) : null
        };

        return this.$httpCacheService.post<LocalizeResult>(`/rest/vegobjekt/${contractId}/localize2`, localizeArgs, description('Stedfeste vegobjekt'))
            .then(server.unwrapResponseData);
    }

    localizeFeaturesWithSkriv(contractId: string, roadObjectIds: string[], roadSegment: RoadSegment, positions: LatLng[], overWritingRoadCategory: string): IPromise<LocalizeResult[]> {
        let utmPositions: PositionJson[];
        if (positions) {
            utmPositions = positions.map(MapUtils.wgs84ToUtm33);
        }
        const localizeArgs: LocalizeWithSkrivArguments = {
            roadObjectIds,
            roadSegment,
            overWritingRoadCategory: overWritingRoadCategory === 'null' ? null : overWritingRoadCategory,
            positions: utmPositions
        };

        return this.$httpCacheService.post<LocalizeResult[]>(`/rest/vegobjekt/${contractId}/stedfesting`, localizeArgs, description('Stedfeste vegobjekter'))
            .then(server.unwrapResponseData);
    }

    startAutoLocalize2(contractId: string, rots: number[]): IPromise<number> {
        return this.$httpCacheService.post<number>('/rest/vegobjekt/' + contractId + '/autostedfest', rots,
            description('Automatisert stedfesting')).then(server.unwrapResponseData);
    }

    readContract(contractId: string): angular.IPromise<Contract> {
        return this.$httpCacheService.get<Contract>(`/rest/contract/${contractId}`, description('Lese kontrakt'))
            .then(server.unwrapResponseData)
            .then((contract: Contract) => {
                return contract;
            });
    }

    saveContract(contract: Contract): IPromise<void> {
        return this.$httpCacheService.post<void>(`/rest/contract/${contract.id}`, contract, description('Lagre kontrakt'))
            .then(server.unwrapResponseData)
    }

    createContract(contract: Contract, addDefaultMilestones: boolean): IPromise<Contract> {
        const contractGroupParam = (contract.contractGroup && contract.contractGroup.length > 0) ? `&contractgroupId=${contract.contractGroup}` : ``;
        return this.$httpCacheService.post<Contract>(`/rest/contract?addDefaultMilestones=${addDefaultMilestones}${contractGroupParam}`, contract,
                                         description('Opprette kontrakt'))
            .then(server.unwrapResponseData);
    }

    readFiles(contractId: string, destination: Destination): IPromise<ContractFile[]> {
        return this.$httpCacheService.get<ContractFile[]>('/rest/contract/' + contractId + '/file' + '?destination=' + destination, description('Lese filer'))
            .then(server.unwrapResponseData);
    }

    readFileContentAsLines(contractId: string, fileId: string): IPromise<SosiFileLine[]> {
        return this.$httpCacheService.get<SosiFileLine[]>('/rest/contract/' + contractId + '/file/' + fileId + '/content?lines=true',
                              description('Lese innholdet i fil, med linjedata'))
            .then(server.unwrapResponseData);
    }

    deleteFile(contractId: string, fileId: string): IPromise<void> {
        return this.$httpCacheService.delete<void>('/rest/contract/' + contractId + '/file/' + fileId, description('Slette fil'))
            .then(server.unwrapResponseData);
    }

    getFile(contractId: string, fileId: string, includeValidationIssues: boolean): IPromise<ContractFile> {
        const validationIssuesParams = includeValidationIssues ? '?include_validation_issues=true' : '';
        return this.$httpCacheService.get<ContractFile>('/rest/contract/' + contractId + '/file/' + fileId + validationIssuesParams,
                              description('Lese fil'))
            .then(server.unwrapResponseData);
    }

    saveFkbAliases(fkbAliases: FkbAlias[]): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/fkbalias', fkbAliases, description('Lagre FKB-objekttypenavn'));
    }

    getOutdatedFeatureAttributes(contractId: string, featureTypeId: number): IPromise<{ [key: string]: number[]}> {
        return this.$httpCacheService.get<{ [key: string]: number[]}>('/rest/contract/' + contractId + '/feature/outdated_attributes?featuretypeid=' + featureTypeId,
                                                          description('Hent utdaterte egenskaper'))
            .then(server.unwrapResponseData);
    }

    updateAllSidePositions(contractId: string, sourceFeatureId: string, wantedSidePosition: SidePosition, visibleFeatures: string[]): IPromise<Feature[]> {
        return this.$httpCacheService.put<Feature[]>(`/rest/contract/${contractId}/side-position-from/${sourceFeatureId}`, {visibleFeatures, sidePosition: wantedSidePosition})
            .then(server.unwrapResponseData);
    }

    updateSidePosition(contractId: string, sidePositionUpdates: SidePositionEdit[]): IPromise<Feature[]> {
        return this.$httpCacheService.post<Feature[]>('/rest/contract/' + contractId + '/sideposition', sidePositionUpdates,
                                     description('Oppdater sideposisjon'))
            .then(server.unwrapResponseData);
    }

    updateAllLanes(contractId: string, sourceFeatureId: string, wantedLane: string, visibleFeatures: string[]): IPromise<Feature[]> {
        return this.$httpCacheService.put<Feature[]>(`/rest/contract/${contractId}/lane-from/${sourceFeatureId}`, {wantedFeatures: visibleFeatures, lane: wantedLane})
            .then(server.unwrapResponseData);
    }

    updateLane(contractId: string, laneUpdates: LaneUpdate[]): IPromise<Feature[]> {
        return this.$httpCacheService.post<Feature[]>('/rest/contract/' + contractId + '/lane', laneUpdates, description('Oppdater kjørefelt'))
            .then(server.unwrapResponseData);
    }

    static getLastModified(file: File): number {
        if (file.lastModified) {
            // https://www.w3.org/TR/FileAPI/#dfn-lastModified
            return file.lastModified;
        } else {
            return new Date().getTime();
        }
    }

    uploadFile(contractId: string, destination: Destination, file: LocalFile): IHttpPromise<ContractFile | undefined> {
        const fd = new FormData();
        fd.append('filename', file.name);
        fd.append('lastModified', server.getLastModified(file.localFile).toString());

        fd.append('file', file.localFile);
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/file' + '?destination=' + destination, fd, {
            transformRequest: angular.identity,
            headers: {
                'Content-Type': undefined
            },
            description: 'Laste opp fil ' + file.name
        } as angular.IRequestShortcutConfig);
    }

    confirmFileOperation(contractId: string, fileId: string, operation: Operation): IPromise<ContractFile> {
        return this.$httpCacheService.post<ContractFile>(`rest/contract/${contractId}/file/${fileId}?operation=${operation}`, {}, description(`Sette operasjon på objekter fra fil`))
            .then(server.unwrapResponseData);
    }

    confirmManyFileOperations(contractId: string, fileIds: string[], operation: Operation): IPromise<ContractFile[]> {
        return Promise.all(fileIds.map(fileId => this.confirmFileOperation(contractId, fileId, operation)));
    }

    uploadExcelFile(contractId: string, file: File): IHttpPromise<ExcelParseResult> {
        const fd = new FormData();
        fd.append('filename', file.name);
        fd.append('file', file);
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/excelobjectlist', fd, {
            transformRequest: angular.identity,
            headers: {
                'Content-Type': undefined
            },
            description: 'Last opp Excel-fil med objektliste'
        } as angular.IRequestShortcutConfig);
    }

    countContractsWithName(name: string): IPromise<number> {
        return this.$httpCacheService.get<number>('/rest/contract/count?name=' + name, description('Hente antall kontrakter med navn ' + name))
            .then(server.unwrapResponseData);
    }

    countContractsWithFilter(showArchived: boolean, contractType: ContractType | 'all', searchTerm: String, showOtherPeoplesContracts: boolean): IPromise<number> {
        const url = '/rest/contract/count_contracts_with_filter';

        let queryParams = '?';
        queryParams += 'showArchived=' + showArchived;
        queryParams += '&contractType=' + contractType;
        queryParams += '&searchTerm=' + searchTerm;
        queryParams += '&showOtherPeoplesContracts=' + showOtherPeoplesContracts;

        return this.$httpCacheService.get<number>(url + queryParams, {description: 'Hente antall kontrakter med dette filteret'} as angular.IRequestShortcutConfig)
            .then(response => server.unwrapResponseData(response));
    }

    listContracts(force: boolean, page: number, showArchived: boolean, contractType: ContractType | 'all', searchTerm: String, showOtherPeoplesContracts: boolean, sortByField: String, isAscending: boolean): IPromise<Contract[]> {
        const url = '/rest/contract';

        let queryParams = '?';
        queryParams += 'numberPerPage=50';
        queryParams += '&page=' + page;
        queryParams += '&showArchived=' + showArchived;
        queryParams += '&contractType=' + contractType;
        queryParams += '&searchTerm=' + searchTerm;
        queryParams += '&showOtherPeoplesContracts=' + showOtherPeoplesContracts;
        queryParams += '&sortByField=' + sortByField;
        queryParams += '&isAscending=' + isAscending;

        return this.$httpCacheService.get<Contract[]>(url + queryParams, {force, description: 'Hente kontraktliste'} as angular.IRequestShortcutConfig)
            .then(response => server.unwrapResponseData(response));
    }

    downloadFiles(contractId: string, files: ContractFile[]): void {
        const urls: string[] = [];
        angular.forEach(files, function (file) {
            urls.push('/rest/contract/' + contractId + '/file/' + file.id + '/content/' + file.name);
        });

        this.downloadFilesByURL(urls);
    }

    downloadFeaturesAsSosi(contractId: string, featureTypeIdsWithValidOperations: FeatureTypeIdWithValidOperations, nvdb123Mode: boolean, srid: string,
                           destination: 'NVDB' | 'FKB', projectRef: string): void {
        const projectRefParam = projectRef ? '&projectRef=' + projectRef : '';
        const sridParam = destination === 'FKB' ? '&srid=' + srid : '';
        const urls = Object.entries(featureTypeIdsWithValidOperations).map(([featureTypeId, validOperations]) =>
                Array.from(validOperations.values()).map((operation: Operation) => `/rest/contract/${contractId}/feature/as_sosi?featuretypeid=${featureTypeId}&forNvdb123=${nvdb123Mode}&destination=${destination}${projectRefParam}${sridParam}&operation=${operation}`)
            ).reduce((prev, curr) => prev.concat(...curr));
        this.downloadFilesByURL(urls);
    }

    downloadFilesByURL(urls: string[]): void {
        const server = this;
        this.downloadFile(urls[0]);
        if (urls.length > 1) {
            window.setTimeout(function () { /* Wait a second between each because of IE */
                server.downloadFilesByURL(urls.slice(1));
            }, 1000);
        }
    }

    downloadFile(url: string): void {
        /* Pre-fetch the URL to get a proper HTTP status, as we're not able to get the status code from the
         * IFRAME.
         */

        this.$httpCacheService.get(url)
            .then(() => {
                /* Creates an invisible iframe and points it to the url */
                const ifrm = document.createElement('IFRAME');
                ifrm.setAttribute('style', 'display:none;');
                ifrm.setAttribute('src', url);
                ifrm.style.width = 0 + 'px';
                ifrm.style.height = 0 + 'px';

                document.body.appendChild(ifrm);
                window.setTimeout(function () {
                    document.body.removeChild(ifrm);
                    /* Removes iframe after 30 sec */
                }, 30000);
            },
            (response) => {
                this.messages.danger('Kunne ikke hente SOSI. ' + response.data.messages[0]);
            });
    }

    static unwrapResponseData<T>(response: IHttpResponse<T>): T {
        return response.data;
    }

    updateContractMilestone(contractId: string, milestone: ContractMilestone): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/milestone/' + milestone.id, milestone, description('Oppdatere milepæl'));
    }

    createContractMilestone(contractId: string, milestone: ContractMilestone): IHttpPromise<ContractMilestone> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/milestone', milestone, description('Opprette milepæl'));
    }

    deleteContractMilestone(contractId: string, milestoneId: string): IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/contract/' + contractId + '/milestone/' + milestoneId, description('Slette milepæl'));
    }

    getFeaturesInArea(typeId: number, searchArea: BoundingBox, includeRelations: boolean): IPromise<NvdbFeature[]> {
        const x1 = searchArea.southWest.lng;
        const y1 = searchArea.southWest.lat;
        const x2 = searchArea.northEast.lng;
        const y2 = searchArea.northEast.lat;
        const include = 'metadata,egenskaper,geometri,lokasjon' + (includeRelations ? ',relasjoner' : '');
        return this.$httpCacheService.get<NvdbFeature[]>(`/rest/vegobjekter?typeId=${typeId}&srid=${searchArea.srid}&include=${include}&x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}`, description('Hente vegobjekter'))
            .then(server.unwrapResponseData);
    }

    getRoadSegmentsInArea(x1: number, y1: number, x2: number, y2: number): IPromise<RoadSegment[]> {
        return this.$httpCacheService.get('/rest/roadnet/?x1=' + x1 + '&y1=' + y1 + '&x2=' + x2 + '&y2=' + y2, description('Hente veglenker'))
            // errors some times occur, but concequences are minior, as most of the area already has roads from previous calls.
            // instead of handling various errors we ignore them, and hope it works out next time (the user pans the map)
            // NB! an error-box will still pop for the user.
            .then(server.unwrapResponseData, () => null);
    }

    submitRegistrationToNvdb(contractId: string, featureTypeIds: number[], effectDate: string, approveChangedLocationals: boolean | null, projectRef?: string): IPromise<NvdbSubmission[] | LocalizationChange[]> {
        const params: NvdbSubmissionRequest = {featureTypeIds, effectDate, projectRef, approveChangedLocationals};
        return this.$httpCacheService.post<NvdbSubmission[]>('/rest/contract/' + contractId + '/submit_registration_to_nvdb', params,
                                                 description('Registrere vegobjekter i NVDB'))
            .then(server.unwrapResponseData);
    }

    // This will overwrite any conflicting changes in NVDB. please use only in emergencies.
    dangerouslySubmitRegistrationToNvdb(contractId: string, featureTypeIds: number[], effectDate: string, approveChangedLocationals: boolean | null, projectRef?: string): IPromise<NvdbSubmission[] | LocalizationChange[]> {
        const params: NvdbSubmissionRequest = {featureTypeIds, effectDate, projectRef, approveChangedLocationals};
        return this.$httpCacheService.post<NvdbSubmission[]>('/rest/contract/' + contractId + '/dangerously_submit_registration_to_nvdb',
                                                 params, description('Registrere vegobjekter i NVDB'))
            .then(server.unwrapResponseData);
    }


    submitClosingsToNvdb(contractId: string, typeIdsForDeletion: number[]): IPromise<MessageDataResponse<NvdbSubmission[]>> {
        return this.$httpCacheService.post<MessageDataResponse<NvdbSubmission[]>>('/rest/contract/' + contractId + '/submit_closings_to_nvdb', typeIdsForDeletion,
                                                                      description('Slette vegobjekter i NVDB'))
            .then(server.unwrapResponseData);
    }

    listSubmittedNvdbRegistrations(contractId: string): IPromise<NvdbSubmission[]> {
        return this.$httpCacheService.get<NvdbSubmission[]>('/rest/contract/' + contractId + '/nvdb_submission/registration', description('Hente registreringer i NVDB'))
            .then(server.unwrapResponseData);
    }

    removeNvdbDeletion(contractId: string, deletionId: number): IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/contract/' + contractId + '/nvdb_feature/' + deletionId, description('Fjerne vegobjekt fra slettemanus'));
    }

    getSubmittabilityReport(contractId: string, featureTypeIds: number[]): IPromise<SubmitabilityReport> {
        return this.$httpCacheService.post<SubmitabilityReport>('/rest/contract/' + contractId + '/submittability_report', featureTypeIds,
                                                    description('Hente endringssett valideringsoversikt'))
            .then(server.unwrapResponseData);
    }

    readContractCompleteness(contractId: string): IPromise<CompletenessItem[]> {
        return this.$httpCacheService.get<CompletenessItem[]>('/rest/contract/' + contractId + '/completeness', description('Hente kompletthetsoversikt'))
            .then(server.unwrapResponseData);
    }


    readAssociationsMulti(contractId: string): IPromise<AssociationsMultiResponse> {
        return this.$httpCacheService.get<AssociationsMultiResponse>('/rest/contract/' + contractId + '/feature/association?format=multi', description('Hente sammenkoblinger'))
            .then(server.unwrapResponseData);
    }

    readAssociationsList(contractId: string): IPromise<Association[]> {
        return this.$httpCacheService.get<Association[]>('/rest/contract/' + contractId + '/feature/association?format=list', description('Hente sammenkoblinger'))
            .then(server.unwrapResponseData);
    }

    checkValidRelationship(parent: number, child: number): IPromise<boolean> {
        return this.$httpCacheService.get<boolean>(`/rest/contract/validRelationship?parentTypeId=${parent}&childTypeId=${child}`, description('Sjekke gyldig sammenkobling'))
            .then(server.unwrapResponseData);
    }

    createAssociations(contractId: string, associationCriterion: AutomaticAssociationParameters): IPromise<AssociationResult> {
        return this.$httpCacheService.post<AssociationResult>('/rest/contract/' + contractId + '/feature/association/create', associationCriterion,
                                                  description('Skape sammekoblinger'))
            .then(server.unwrapResponseData);
    }

    createAssociationManually(contractId: string, associationParameters: ManualAssociationParameters): IPromise<AssociationResult> {
        return this.$httpCacheService.post<AssociationResult>('/rest/contract/' + contractId + '/feature/association/create_manually', associationParameters, description('Skape manuell sammekobling'))
            .then(server.unwrapResponseData);
    }

    deleteAssociation(contractId: string, associationId: string): IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/contract/' + contractId + '/feature/association/' + associationId, description('Slette sammenkobling'));
    }

    undeleteAssociation(contractId: string, associationId: string): IHttpPromise<AssociationResult> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/feature/association/' + associationId + '/undelete',
            {},
            description('Gjenopprette sammenkobling'));
    }

    resolveIssuesAsNotApplicable(contractId: string, issueId: string, resolveSimilar: boolean): IPromise<string[]> {
        return this.$httpCacheService.post<string[]>('/rest/contract/' + contractId + '/validationissue/resolve/notapplicable',
            {issueId, resolveSimilar}, description('Markere valideringsfeil kvittert'))
            .then(server.unwrapResponseData);
    }

    deleteContract(contractId: string): angular.IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/contract/' + contractId, description('Slette kontrakt'));
    }

    toggleArchiveContract(contractId: string, archiveState: string): IHttpPromise<void> {
        const archiveStateJson = (archiveState == 'ARCHIVED') ? '"ARCHIVED"' : '"OPEN"';
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/archive/', archiveStateJson, description('Arkiver kontrakt'));
    }

    setContractBoundary(contractId: string, boundaryWkt: string): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/boundary', boundaryWkt, description('Sett kontraktsområde'));
    }

    getContractBoundary(contractId: string): IPromise<string> {
        return this.$httpCacheService.get<string>('/rest/contract/' + contractId + '/boundary', description('Hent kontraktsområde'))
            .then(server.unwrapResponseData);
    }

    saveContractMapComment(contractId: string, mapComment: MapComment): IPromise<MapComment> {
        return this.$httpCacheService.post<MapComment>('/rest/contract/' + contractId + '/mapcomment', mapComment,
                                           description('Lagre kartkommentar'))
            .then(server.unwrapResponseData);
    }

    deleteContractMapComment(contractId: string, mapComment: MapComment): IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/contract/' + contractId + '/mapcomment/' + mapComment.id, description('Slette kartkommentar'));
    }

    static addUrlParameter(url: string, parameter: string, value: string): string {
        if (value !== undefined) {
            if (url.indexOf('?') == -1) {
                url += ('?' + parameter + '=' + value);
            } else {
                url += ('&' + parameter + '=' + value);
            }
        }
        return url;
    }

    verifyAndSetPassword(username: string, password: string, token: string): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/user/verifysetpassword',
            {username, password, token},
            description('Verifisere epost og sette passord')
        );
    }

    verifySystemCreatedAndSetPassword(username: string, password: string, token: string, name: string): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/user/systemverifysetpassword',
            {username, password, token, name},
            description('Verifisere epost og sette passord')
        );
    }

    requestPasswordReset(emailAddress: string): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/user/forgotpassword', {username: emailAddress}, description('Be om nytt passord'));
    }

    resetPassword(username: string, password: string, token: string): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/user/resetpassword',
            {username, password, token},
            description('Sette nytt passord')
        );
    }

    removeUserFromContract(contractId: string, username: string): IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/contract/' + contractId + '/contributors/' + username, description('Fjern kontrakstilgang'));
    }

    addUserToContract(contractId: string, user: string): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/contributors/add', user, description('Legge til kontrakstilgang'));
    }

    addContractToGroup(contractId: string, groupId: string): IHttpPromise<void> {
        return this.$httpCacheService.post(
            `/rest/contractgroup/${groupId}/addcontract`,
            {contractid: contractId},
            description('Legge kontrakt til kontraktgruppe'));
    }

    removeContractFromGroup(contractId: string, groupId: string): IHttpPromise<void> {
        return this.$httpCacheService.post(
            `/rest/contractgroup/${groupId}/removecontract`,
            {contractid: contractId},
            description('Fjerne kontrakten fra kontraktgruppen'));
    }

    getContractGroupList(): IPromise<MessageDataResponse<ContractGroupWithData[]>> {
        return this.$httpCacheService.get<MessageDataResponse<ContractGroupWithData[]>>(
            '/rest/contractgroup/list',
            description('Hente kontraktgrupper'))
            .then(server.unwrapResponseData);
    }

    getContractGroupForContract(contractId: string): IPromise<MessageDataResponse<ContractGroup>> {
        return this.$httpCacheService.get<MessageDataResponse<ContractGroup>>(
            '/rest/contractgroup/groupforcontract/' + contractId,
            description('Hente kontraktgruppe for kontrakten'))
            .then(response => {
                console.dir(response);
                return server.unwrapResponseData(response);
            });
    }

    getAllRoleMappings(): IPromise<{ [key: string]: string[] }> {
        return this.$httpCacheService.get<{ [key: string]: string[] }>('/rest/user/usersbyroles',
            description('Hente brukere gruppert med roller'))
            .then(server.unwrapResponseData);
    }

    setRolesForUser(roles: string[], user: string): IHttpPromise<void> {
        return this.$httpCacheService.put('/rest/user/' + user + '/roles', roles, description('Sette roller for bruker'));
    }

    getAllUsers(include: string, usernameFilter: string): angular.IPromise<User[]> {
        let queryParams = '';
        if (include) {
            queryParams += '?include=' + include;
        }
        if (usernameFilter) {
            queryParams += '&filter=' + usernameFilter;
        }
        return this.$httpCacheService.get<User[]>('/rest/user' + queryParams, description('Hente alle brukere med roller')).then(server.unwrapResponseData);
    }

    deleteUser(username: string): IHttpPromise<void> {
        return this.$httpCacheService.delete('/rest/user/' + username, description('Slett bruker ' + username));
    }

    getVersionInfo(): IPromise<VersionInfo> {
        return this.$httpCacheService.get<VersionInfo>('/status/version', description('Hente versjonsinformasjon')).then(server.unwrapResponseData);
    }

    setContractLocks(contractId: string, lockState: LockState): IHttpPromise<void> {
        const lockStateJson = (lockState == LockState.LOCKED) ? '"LOCKED"' : '"OPEN"';
        return this.$httpCacheService.put('/rest/contract/' + contractId + '/lock', lockStateJson, description('Oppdatere kontraktslåser'));
    }

    readContractLocks(contractId: string): IPromise<{ [key: string]: ContractLock[] }> {
        return this.$httpCacheService.get<{ [key: string]: ContractLock[] }>('/rest/contract/' + contractId + '/lock', description('Hente kontraktlåser'))
            .then(server.unwrapResponseData);
    }

    updateContractLock(contractId: string, destination: string, typeId: number, state: LockState): IHttpPromise<void> {
        const lock = {featureTypeId: typeId, state: LockState[state]}; // Map enum to string
        return this.$httpCacheService.put('/rest/contract/' + contractId + '/lock/' + destination + '/' + typeId, lock, description('Oppdatere låsstatus'));
    }

    updateContractFeatureLock(contractId: string, destination: string, featureTypeId: number, featureId: string, state: LockState): IHttpPromise<void> {
        const lock = {featureTypeId, featureId, state: LockState[state]}; // Map enum to string
        return this.$httpCacheService.put('/rest/contract/' + contractId + '/lock/' + destination + '/' + featureTypeId + '/' + featureId, lock, description('Oppdatere låsstatus'));
    }

    getContractEvents(contractId: string): IPromise<Event[]> {
        return this.$httpCacheService.get<Event[]>('/rest/contract/' + contractId + '/events', description('Hente hendelser for kontrakt'))
            .then(server.unwrapResponseData)
            .then(function (events) {
                return events.map(function (event) {
                    event.occurredInstant = new Date(event.occurredInstant);
                    return event;
                });
            });
    }

    saveComments(comments: Comment[]): IPromise<Comment[]> {
        const promises = comments.map(comment =>
            this.$httpCacheService.post<Comment>('/rest/contract/' + comment.contractId + '/comments', comment, description('Lagre kommentar'))
                .then(server.unwrapResponseData));
        return this.$q.all(promises);
    }

    saveComment(comment: Comment): IPromise<Comment> {
        return this.$httpCacheService.post<Comment>('/rest/contract/' + comment.contractId + '/comments', comment, description('Lagre kommentar'))
            .then(server.unwrapResponseData);
    }

    deleteComments(contractId: string, commentIds: string[]): Promise<void> {
        return Promise.all(commentIds.map(commentId => this.$httpCacheService.delete<void>('/rest/contract/' + contractId + '/comments/' + commentId, description('Slette kommentar')))).then();
    }

    deleteFeature(contractId: string, featureId: string): IPromise<IHttpResponse<void>> {
        return this.$httpCacheService.delete(`/rest/contract/${contractId}/feature/${featureId}`, description('Slett feature'))
            .then((result: IHttpResponse<void>) => {
                return result;
            });
    }

    deleteFeatures(contractId: string, featureIds: string[]): IPromise<unknown> {
        const promises = featureIds.map(featureId => this.$httpCacheService.delete<void>('/rest/contract/' + contractId + '/feature/' + featureId, description('Slett feature')));
        return this.$q.all(promises);
    }

    importNvdbFeatureById(contractId: string, typeId: number, nvdbFeatureId: string, operation: Operation): IHttpPromise<MessageStatusResponse> {
        return this.$httpCacheService.get(`/rest/contract/${contractId}/feature/nvdbimport/${typeId}/${nvdbFeatureId}/${operation}`);
    }

    importNvdbFeatureByIdAsType(contractId: string, typeId: number, nvdbFeatureId: string, newtypeId: number): IHttpPromise<MessageStatusResponse> {
        return this.$httpCacheService.get(`/rest/contract/${contractId}/feature/nvdbimportastype/${typeId}/${nvdbFeatureId}/${newtypeId}`);
    }


    copyDatafangstFeatures(contractId: string, featureIds: string[]): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/feature/copy', {
            featureIds
        }, description('Kopier feature'));
    }

    copyFeatureGeometry(contractId: string, featureIds: string[], typeId: number): IHttpPromise<void> {
        return this.$httpCacheService.post('/rest/contract/' + contractId + '/feature/copy-with-type', {
            featureIds,
            typeId
        }, description('Kopier geometri til nye features'));
    }

    getCopiableGeometries(contractId: string, selectedFeatureIds: string[]): IHttpPromise<CopiableGeometryFeature[]> {
        const params = `selected=${selectedFeatureIds.join(',')}`;
        return this.$httpCacheService.get(`/rest/contract/${contractId}/feature/copiable_geometries?${params}`, description('Hent mulige geometrikopieringsoperasjoner'));
    }

    copyGeometryBetweenFeatures(contractId: string, from: string, to: string[]): IHttpPromise<void> {
        return this.$httpCacheService.post(`/rest/contract/${contractId}/feature/copy_feature_geometries`, {from, to}, description('Kopier geometri mellom vegobjekter'));
    }

    cutFeatureGeometry(contractId: string, featureId: string, pos: L.LatLng): IHttpPromise<void> {
        const path = '/rest/contract/' + contractId + '/feature/cut/' + featureId;
        return this.$httpCacheService.post(path, { easting: pos.lng, northing: pos.lat}, description('Klipper feature'));
    }

    updateFeatureGeometry(contractId: string, featureTypeId: number, featureId: string, wkt: string, shouldRelocalize: boolean): IHttpPromise<void> {
        const url = '/rest/contract/' + contractId + '/featuretype/' + featureTypeId + '/feature/' + featureId
            + '/editgeometry' + '?shouldRelocalize=' + shouldRelocalize;
        return this.$httpCacheService.put(url, wkt, description('Rediger geometri'));
    }

    updateHeightReference(contractId: string, featureId: string, heightReference: HeightReference): IPromise<Feature> {
        const url = '/rest/contract/' + contractId + '/feature/' + featureId
            + '/editHeightReference' + '?shouldRelocalize=false' ;
        return this.$httpCacheService
          .put<Feature>(
            url,
            { heightReference },
            description("Rediger høydereferanse")
          )
          .then(server.unwrapResponseData);
    }

    checkIfContractsHaveChanges(contractIds: string[]): IPromise<{ [key: string]: boolean }> {
        const params: Description = {
            params: {
                contractIds
            },
            description: 'Sjekk hvilke kontrakter det er gjort endringer på etter du besøkte dem sist'
        };
        return this.$httpCacheService.get<{ [key: string]: boolean }>('/rest/contractchanges', params)
            .then(server.unwrapResponseData);
    }

    getPendingAutoLocalizingCount(contractId: string): IPromise<number> {
        return this.$httpCacheService.get<number>('/rest/vegobjekt/' + contractId + '/autostedfest/antall', description('Hent antall vegobjekter som blir stedfestet i bakgrunnen'))
            .then(server.unwrapResponseData);
    }

    addSidePositionFromParent(contractId: string, featureIds: string[]): IPromise<Feature[]> {
        return this.$httpCacheService.post<Feature[]>(`/rest/vegobjekt/sideposisjon_fra_mor?contractId=${contractId}`, featureIds, description('Legg til sideposisjon fra mor'))
            .then(server.unwrapResponseData);
    }

    toggleFeature(feature: string): IPromise<string[]> {
        return this.$httpCacheService.get<string[]>(`/session/toggleFeature/${feature}`,
            description('Toggle eksperimentell funksjonalitet'))
            .then(server.unwrapResponseData);
    }

    findUsers(contractId: string, searchText: string): IPromise<Contributor[]> {
        const params: Description = {
            params: {
                search: searchText
            },
            description: 'Søk etter brukere'
        };
        console.log('Searching for users', params);
        return this.$httpCacheService.get<User[]>('/rest/contract/' + contractId + '/contributor', params)
            .then(server.unwrapResponseData);
    }
}

server.$inject = ['$httpCacheService', '$q', 'messages'];

interface Description extends angular.IRequestShortcutConfig {
    description: string;
}

function description(desc: string): Description {
    return {description: desc};
}

export type LocalizeArguments = {
    roadObjectId: string,
    roadSegmentJson: RoadSegment,
    startAnchor: PositionJson,
    endAnchor: PositionJson

};

export type LocalizeWithSkrivArguments = {
    roadObjectIds: string[],
    roadSegment: RoadSegment,
    overWritingRoadCategory: string;
    positions: PositionJson[]
};

export type LocalizeResult = {
    features: DisplayFeature[];
    success: boolean;
    message: string
};
